import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  ListGroup,
  Spinner,
  Badge,
  Alert,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import {
  collection,
  query,
  where,
  onSnapshot,
  getDoc,
  doc
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { firestore } from '../views/firebaseConfig';
import MessageDetailModal from './MessageDetailModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faList,
  faWifi,
  faUsers,
  faDollarSign,
  faTrophy,
  faBolt
} from '@fortawesome/free-solid-svg-icons';
import './MessagesModal.css';

const FILTERS = [
  { key: 'All',                icon: faList,       color: '#aaa' },
  { key: 'Nearby Player',      icon: faWifi,       color: '#aaa' },
  { key: 'Social Chat',        icon: faUsers,      color: '#aaa' },
  { key: 'Shoparound',         icon: faDollarSign, color: '#aaa' },
  { key: 'Event Notification', icon: faTrophy,     color: '#aaa' },
  { key: 'Active Conversation',icon: faBolt,       color: '#aaa' }
];

async function buildConvoSummary(docSnap, currentUserID) {
  const data = docSnap.data();
  const convoId = docSnap.id;
  const interestedId = data.interestedId || '';
  const receiverId   = data.receiverId   || '';
  const otherPartyID = receiverId === currentUserID ? interestedId : receiverId;

  // Compute unread count and latest message
  let unreadCount = 0;
  let latestMessage = '';
  let latestTs = null;

  (data.conversation || []).forEach(msg => {
    const ts = msg.timestamp?.toDate?.() || msg.timestamp;
    if (msg.senderID !== currentUserID && msg.readStatus === 'unread') {
      unreadCount++;
    }
    if (!latestTs || (ts && ts > latestTs)) {
      latestTs = ts;
      latestMessage = msg.text;
    }
  });

  // Fetch other party's nickname
  let nickname = 'Unknown User';
  let profileImageUrl = '';
  let listingImageUrl = '';
  let listingPrice = '';
  if (otherPartyID) {
    try {
      const up = await getDoc(doc(firestore, 'userprofiles', otherPartyID));
      if (up.exists()) { nickname = up.data().nickname || nickname; profileImageUrl = up.data().profileImageUrl || ''; }
    } catch (e) {
      console.error('Error fetching nickname:', e);
    }
  }

  // Fetch sale title
  let saleTitle = '';
  if (data.forsaleId) {
    try {
      const fs = await getDoc(doc(firestore, 'forsale', data.forsaleId));
      if (fs.exists()) { const sale = fs.data(); saleTitle = sale.title || ''; listingImageUrl = sale.image1 || sale.imageUrl || sale.imageUrls?.[0] || ''; listingPrice = sale.price || ''; }
    } catch (e) {
      console.error('Error fetching forsale title:', e);
    }
  }

  // Determine category from saleTitle
  let category = 'All';
  if (saleTitle === 'Nearby Player') {
    category = 'Nearby Player';
  } else if (saleTitle === 'Social Chat') {
    category = 'Social Chat';
  } else if (saleTitle === 'Event Notification') {
    category = 'Event Notification';
  } else if (saleTitle === 'Active Conversation') {
    category = 'Active Conversation';
  } else if (saleTitle) {
    category = 'Shoparound';
  }

  return {
    id: convoId,
    otherPartyID,
    otherPartyNickname: nickname,
    profileImageUrl,
    listingImageUrl,
    listingPrice,
    saleTitle,
    saleId: data.forsaleId,
    category,
    unreadCount,
    latestMessage,
    latestMessageTimestamp: latestTs,
    lastModified: data.lastModified?.toDate?.() || null
  };
}


function ConversationItem({ convo, onSelect }) {
  const filterObj = FILTERS.find(f => f.key === convo.category);
  return (
    <ListGroup.Item
      action
      onClick={() => onSelect(convo)}
      className="conversation-item"
    >
      <div className="p4r-conversation-layout">
        <div className="p4r-conversation-media">
          <img src={convo.listingImageUrl || convo.profileImageUrl || 'https://placehold.co/96x96/151515/FFD45C?text=P4R'} alt="" />
          {convo.listingImageUrl && convo.profileImageUrl && <img className="p4r-conversation-avatar" src={convo.profileImageUrl} alt="" />}
        </div>
        <div className="p4r-conversation-content">
      <div className="conv-header">
        <div className="conv-title">
          <FontAwesomeIcon icon={filterObj.icon} style={{ color: filterObj.color }} />
          <span className="ms-2">{convo.otherPartyNickname}</span>
        </div>
        <small className="timestamp">
          {convo.latestMessageTimestamp?.toLocaleString([], {
            month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit'
          })}
        </small>
      </div>

      <div className="conv-subheader">
        <Badge bg="secondary">{convo.category}</Badge>
        {convo.saleTitle && <span className="sale-title ms-2">{convo.saleTitle}</span>}
      </div>

      <div className="conv-footer">
        <span className="preview-text">{convo.latestMessage || 'No messages yet.'}</span>
        {convo.unreadCount > 0 && <Badge bg="danger" pill>{convo.unreadCount}</Badge>}
      </div>
      </div></div>
    </ListGroup.Item>
  );
}

export default function MessagesModal({ show, onClose }) {
  const { user, loading: authLoading } = useAuth();
  const currentUserID = user?.uid;
  const inboxColl = collection(firestore, 'globalinbox');

  const [activeView, setActiveView]         = useState('allowed');
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [selectedConvo, setSelectedConvo]   = useState(null);
  const [loadingAllowed, setLoadingAllowed] = useState(true);
  const [loadingBlocked, setLoadingBlocked] = useState(true);

  const [blockedUsers, setBlockedUsers]     = useState(new Set());
  const [mapAllowed, setMapAllowed]         = useState({});
  const [mapBlocked, setMapBlocked]         = useState({});

  useEffect(() => {
    if (!currentUserID) return;
    getDoc(doc(firestore, 'blockedUsers', currentUserID))
      .then(snap => {
        setBlockedUsers(new Set(snap.exists() ? snap.data().blockedList || [] : []));
      })
      .catch(() => setBlockedUsers(new Set()));
  }, [currentUserID]);

  useEffect(() => {
    if (authLoading || !currentUserID) return;
    const q1 = query(
      inboxColl,
      where('interestedId', '==', currentUserID),
      where('blockedConversation', '==', false),
      where(`deletedConversation.${currentUserID}`, '==', 'active')
    );
    const q2 = query(
      inboxColl,
      where('receiverId', '==', currentUserID),
      where('blockedConversation', '==', false),
      where(`deletedConversation.${currentUserID}`, '==', 'active')
    );

    const handleSnap = async snap => {
      const newMap = {};
      await Promise.all(snap.docs.map(async d => {
        try {
          const sum = await buildConvoSummary(d, currentUserID);
          if (sum && !blockedUsers.has(sum.otherPartyID)) {
            newMap[d.id] = sum;
          }
        } catch (e) {
          console.error('buildConvoSummary error:', e);
        }
      }));
      setMapAllowed(newMap);
      setLoadingAllowed(false);
    };

    const unsub1 = onSnapshot(q1, handleSnap);
    const unsub2 = onSnapshot(q2, handleSnap);
    return () => { unsub1(); unsub2(); };
  }, [authLoading, currentUserID, blockedUsers]);

  useEffect(() => {
    if (authLoading || !currentUserID) return;
    const qb = query(inboxColl, where('blockComms', '==', currentUserID));
    const unsub = onSnapshot(qb, async snap => {
      const newMap = {};
      await Promise.all(snap.docs.map(async d => {
        try {
          const sum = await buildConvoSummary(d, currentUserID);
          if (sum) newMap[d.id] = sum;
        } catch (e) {
          console.error('buildConvoSummary error:', e);
        }
      }));
      setMapBlocked(newMap);
      setLoadingBlocked(false);
    });
    return unsub;
  }, [authLoading, currentUserID]);

  if (authLoading || !user) return null;

  const allowedArr = Object.values(mapAllowed)
    .sort((a,b) => (b.lastModified?.getTime()||0) - (a.lastModified?.getTime()||0))
    .filter(c => selectedFilter === 'All' || c.category === selectedFilter);

  const blockedArr = Object.values(mapBlocked)
    .sort((a,b) => (b.lastModified?.getTime()||0) - (a.lastModified?.getTime()||0));

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        size="lg"
        centered
        scrollable
        backdropClassName="messages-backdrop"
        dialogClassName="messages-dialog"
      >
        <Modal.Header closeButton>
          <Modal.Title>Convos</Modal.Title>
        </Modal.Header>


<Modal.Body className="messages-body">
  <div className="messages-toggle">
    <Button
      variant={activeView === 'allowed' ? 'light' : 'outline-light'}
      size="sm"
      onClick={() => { setActiveView('allowed'); setSelectedFilter('All'); }}
    >
      Allowed
    </Button>
    <Button
      variant={activeView === 'blocked' ? 'light' : 'outline-light'}
      size="sm"
      onClick={() => { setActiveView('blocked'); setSelectedFilter('All'); }}
    >
      Blocked
    </Button>
  </div>

  {activeView === 'allowed' && (
    <>
      <div className="filter-icons">
        {FILTERS.map(f => (
          <OverlayTrigger
            key={f.key}
            placement="bottom"
            overlay={<Tooltip id={`tip-${f.key}`}>{f.key}</Tooltip>}
          >
            <Button
              variant={selectedFilter === f.key ? 'light' : 'outline-light'}
              size="sm"
              onClick={() => setSelectedFilter(f.key)}
              className="filter-btn"
            >
              <FontAwesomeIcon icon={f.icon}/>
            </Button>
          </OverlayTrigger>
        ))}
      </div>

      {loadingAllowed ? (
        <div className="text-center py-4"><Spinner size="sm" /></div>
      ) : allowedArr.length === 0 ? (
        <Alert variant="secondary" className="text-center">
          No conversations in “{selectedFilter}.”
        </Alert>
      ) : (
        <ListGroup variant="flush" className="messages-list">
          {allowedArr.map(c => (
            <ConversationItem
              key={c.id}
              convo={c}
              onSelect={setSelectedConvo}
            />
          ))}
        </ListGroup>
      )}
    </>
  )}

  {activeView === 'blocked' && (
    loadingBlocked ? (
      <div className="text-center py-4"><Spinner size="sm" /></div>
    ) : blockedArr.length === 0 ? (
      <Alert variant="secondary" className="text-center">
        No blocked conversations.
      </Alert>
    ) : (
      <ListGroup variant="flush" className="messages-list">
        {blockedArr.map(c => (
          <ConversationItem
            key={c.id}
            convo={c}
            onSelect={setSelectedConvo}
          />
        ))}
      </ListGroup>
    )
  )}
</Modal.Body>


        <Modal.Footer className="py-2">
          <Button variant="outline-light" size="sm" onClick={onClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {selectedConvo && (
        <MessageDetailModal
          show
          onClose={() => setSelectedConvo(null)}
          forsaleId={selectedConvo.saleId}
          interestedId={user.uid}
          receiverId={selectedConvo.otherPartyID}
          conversationId={selectedConvo.id}
        />
      )}
    </>
  );
}
